// @ts-nocheck
import { motion, AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { CouponData } from '../types/campaign';

interface SurpriseGiftModalProps {
    isOpen: boolean;
    onClose: () => void;
    onReveal: () => void;
    couponData: CouponData | null;
}

export default function SurpriseGiftModal({
    isOpen,
    onClose,
    onReveal,
    couponData,
}: SurpriseGiftModalProps) {
    const [isRevealing, setIsRevealing] = useState(false);

    const handleReveal = async () => {
        setIsRevealing(true);
        // Add a small delay for animation effect
        setTimeout(() => {
            onReveal();
        }, 500);
    };

    // Get the campaign logo from metadata
    const campaignLogo =
        couponData?.metadata_display_properties?.marketingScreen?.topImageUri;
    const campaignBrand =
        couponData?.metadata_display_properties?.marketingScreen?.brand;
    const campaignName = campaignBrand
        ? `${campaignBrand} and Niio Art`
        : couponData?.metadata_display_properties?.marketingScreen?.title ||
          couponData?.name ||
          'this campaign';

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    className="fixed inset-0 z-50 flex items-center justify-center px-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                >
                    {/* Backdrop */}
                    <motion.div
                        className="absolute inset-0 bg-black bg-opacity-80 backdrop-blur-sm"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                    />

                    {/* Modal Content */}
                    <motion.div
                        className="relative z-10 w-full max-w-md bg-gray-900 rounded-2xl p-6 shadow-2xl border border-gray-800"
                        initial={{ scale: 0.9, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.9, opacity: 0, y: 20 }}
                        transition={{
                            type: 'spring',
                            stiffness: 300,
                            damping: 25,
                        }}
                    >
                        {/* Close button */}
                        <button
                            onClick={onClose}
                            className="absolute top-4 right-4 text-gray-400 hover:text-white transition-colors duration-200"
                        >
                            <svg
                                className="w-6 h-6"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M6 18L18 6M6 6l12 12"
                                />
                            </svg>
                        </button>

                        {/* Campaign Logo */}
                        {campaignLogo && (
                            <div className="flex justify-center mb-6">
                                <img
                                    src={campaignLogo}
                                    alt="Campaign Logo"
                                    className="h-16 w-auto object-contain"
                                />
                            </div>
                        )}

                        {/* Gift announcement with sparkles */}
                        <div className="text-center space-y-4">
                            <motion.div
                                animate={{
                                    scale: [1, 1.05, 1],
                                }}
                                transition={{
                                    duration: 2,
                                    repeat: Infinity,
                                    ease: 'easeInOut',
                                }}
                                className="text-6xl mb-4"
                            >
                                🎁
                            </motion.div>

                            <h2 className="text-2xl font-bold text-white">
                                Surprise! You've Got a Gift!
                            </h2>

                            <p className="text-gray-300 text-base px-4">
                                You've received a free artcast from{' '}
                                {campaignName}! When you claim this gift, the
                                artcast will be saved to your favorites and will
                                await you whenever you're ready.
                            </p>

                            {/* Reveal Button with green glow */}
                            <motion.button
                                onClick={handleReveal}
                                disabled={isRevealing}
                                className="relative mt-6 px-8 py-3 bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold rounded-full shadow-xl transform transition-all duration-300 hover:scale-105 disabled:opacity-75 disabled:cursor-not-allowed"
                                whileHover={{ scale: 1.05 }}
                                whileTap={{ scale: 0.95 }}
                                style={{
                                    boxShadow:
                                        '0 0 30px rgba(34, 197, 94, 0.5), 0 0 60px rgba(34, 197, 94, 0.3)',
                                }}
                            >
                                {/* Button glow animation */}
                                <motion.div
                                    className="absolute inset-0 rounded-full bg-gradient-to-r from-green-400 to-emerald-400 opacity-75"
                                    animate={{
                                        opacity: [0.5, 0.8, 0.5],
                                    }}
                                    transition={{
                                        duration: 2,
                                        repeat: Infinity,
                                        ease: 'easeInOut',
                                    }}
                                    style={{
                                        filter: 'blur(10px)',
                                        zIndex: -1,
                                    }}
                                />

                                <span className="flex items-center justify-center gap-2">
                                    {isRevealing ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                                            Revealing...
                                        </>
                                    ) : (
                                        <>
                                            Reveal Gift
                                            <span className="text-xl">🎁</span>
                                        </>
                                    )}
                                </span>
                            </motion.button>

                            {/* Sparkle decorations */}
                            <div className="absolute inset-0 pointer-events-none">
                                {[...Array(6)].map((_, i) => (
                                    <motion.div
                                        key={i}
                                        className="absolute text-2xl"
                                        initial={{
                                            opacity: 0,
                                            scale: 0,
                                        }}
                                        animate={{
                                            opacity: [0, 1, 0],
                                            scale: [0, 1, 0],
                                            rotate: [0, 180, 360],
                                        }}
                                        transition={{
                                            duration: 3,
                                            repeat: Infinity,
                                            delay: i * 0.5,
                                            ease: 'easeInOut',
                                        }}
                                        style={{
                                            top: `${20 + i * 15}%`,
                                            left: `${10 + i * 15}%`,
                                        }}
                                    >
                                        ✨
                                    </motion.div>
                                ))}
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
